import { c as _c } from "react/compiler-runtime";
import { Button, ButtonProps, Icon, IconButton, SimpleGrid } from '@chakra-ui/react';
import { useCurrentUrl } from '@src/hooks/useCurrentUrl';
import { scoBackendUrl, startupsUrl } from '@src/lib/startups-url';
import NextLink from 'next/link';
import React, { ReactNode, useMemo } from 'react';
import { FaGoogle, FaLinkedin, FaFacebook, FaSquareXTwitter } from 'react-icons/fa6';
export type SocialLoginButtonsProps = {
  actionLabel?: string;
  buttonLayout?: 'google-callout-grid' | 'google-callout-icons' | 'equal-grid' | 'equal-list' | 'icons-only' | 'google-only';
  buttonProps?: {
    all?: ButtonProps;
    facebook?: ButtonProps;
    google?: ButtonProps;
    linkedIn?: ButtonProps;
    twitter?: ButtonProps;
  };
  returnToHref?: string;
  hubspotFormKeys?: string[];
};
export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = (props: SocialLoginButtonsProps) => {
  const currentFullUrl = useCurrentUrl();
  const returnToUrl = startupsUrl(props.returnToHref || currentFullUrl || '/account');
  const referringUrl = currentFullUrl;
  const buttonLayout = props.buttonLayout || 'google-callout-grid';
  const socialPlatforms = useMemo(() => {
    return {
      google: {
        key: 'google',
        title: 'Google',
        icon: <FaGoogle />,
        href: scoBackendUrl(`/auth/google`, {
          returnToUrl,
          referringUrl,
          hsFormKeys: (props.hubspotFormKeys || [])?.join(',')
        })
      },
      facebook: {
        key: 'facebook',
        title: 'Facebook',
        icon: <FaFacebook />,
        href: scoBackendUrl(`/auth/facebook`, {
          returnToUrl,
          referringUrl,
          hsFormKeys: (props.hubspotFormKeys || [])?.join(',')
        })
      },
      linkedin: {
        key: 'linkedin',
        title: 'LinkedIn',
        icon: <FaLinkedin />,
        href: scoBackendUrl(`/auth/linkedin`, {
          returnToUrl,
          referringUrl,
          hsFormKeys: (props.hubspotFormKeys || [])?.join(',')
        })
      },
      twitter: {
        key: 'twitter',
        title: 'X/Twitter',
        icon: <FaSquareXTwitter />,
        href: scoBackendUrl(`/auth/twitter`, {
          returnToUrl,
          referringUrl,
          track: props.hubspotFormKeys?.join(',')
        })
      }
    };
  }, [currentFullUrl, returnToUrl, referringUrl]);

  /**
   * Specifies SimpleGrid columns for first and second grids, as applicable
   */
  const gridColumns = {
    'google-callout-grid': [[1], [1, 1, 3]],
    'google-callout-icons': [[1], [3]],
    'equal-grid': [[1, 1, 2], [1]],
    'equal-list': [[1], [1]],
    'icons-only': [[4], [4]],
    'google-only': [[1], [1]]
  };
  if (buttonLayout.indexOf('google-only') !== -1) {
    return <SimpleGrid columns={1} gap={'1em'}>
        <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.google} />
      </SimpleGrid>;
  } else if (buttonLayout.indexOf('google-callout') !== -1) {
    return <SimpleGrid columns={gridColumns[buttonLayout][0]} gap={'1em'}>
        <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.google} />
        <SimpleGrid columns={gridColumns[buttonLayout][1]} gap={'0.5em'}>
          <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.linkedin} />
          <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.facebook} />
          <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.twitter} />
        </SimpleGrid>
      </SimpleGrid>;
  } else {
    return <SimpleGrid columns={gridColumns[buttonLayout][0]} gap={'1em'}>
        <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.google} />
        <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.linkedin} />
        <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.facebook} />
        <SocialLoginButton {...props} buttonLayout={buttonLayout} platform={socialPlatforms.twitter} />
      </SimpleGrid>;
  }
};
interface SocialLoginButtonProps {
  platform: {
    key: string;
    title: string;
    href: string;
    icon: ReactNode;
  };
}
export const SocialLoginButton = props => {
  const $ = _c(24);
  let t0;
  if ($[0] !== props.buttonLayout || $[1] !== props.platform.key) {
    t0 = props.platform.key === "google" && (props.buttonLayout || "").indexOf("google-callout") == 0;
    $[0] = props.buttonLayout;
    $[1] = props.platform.key;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isCalledOut = t0;
  const t1 = props.buttonLayout || "";
  let t2;
  if ($[3] !== t1) {
    t2 = t1.indexOf("icon");
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isIconOnly = t2 !== -1;
  let t3;
  if ($[5] !== props.actionLabel || $[6] !== props.platform.title) {
    t3 = [props.actionLabel, props.platform.title].filter(Boolean);
    $[5] = props.actionLabel;
    $[6] = props.platform.title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const tooltipLabel = t3.join(" with ");
  const buttonSize = isCalledOut ? "md" : "sm";
  let buttonLabel;
  if (isIconOnly) {
    if (isCalledOut) {
      buttonLabel = tooltipLabel;
    } else {
      buttonLabel = props.platform.title;
    }
  } else {
    if (!isCalledOut && (props.buttonLayout || "").indexOf("callout") !== -1) {
      buttonLabel = props.platform.title;
    } else {
      buttonLabel = tooltipLabel;
    }
  }
  const ButtonComponent = isIconOnly && !isCalledOut ? IconButton : Button;
  const IconToRender = props.platform.icon;
  let t4;
  if ($[8] !== isCalledOut || $[9] !== isIconOnly || $[10] !== props.platform.icon) {
    t4 = isIconOnly && !isCalledOut ? {
      icon: props.platform.icon
    } : {
      leftIcon: props.platform.icon
    };
    $[8] = isCalledOut;
    $[9] = isIconOnly;
    $[10] = props.platform.icon;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = props.buttonProps?.all;
  const t6 = props.buttonProps ? props.buttonProps[props.platform.key] : null;
  let t7;
  if ($[12] !== IconToRender) {
    t7 = <Icon asChild={true} boxSize="1em">{IconToRender}</Icon>;
    $[12] = IconToRender;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== ButtonComponent || $[15] !== buttonLabel || $[16] !== buttonSize || $[17] !== props.platform.href || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== tooltipLabel) {
    t8 = <ButtonComponent aria-label={tooltipLabel} as={NextLink} href={props.platform.href} marginY="0.125em" size={buttonSize} title={tooltipLabel} variant="outlineGhost" {...t4} {...t5} {...t6}>{t7}{buttonLabel}</ButtonComponent>;
    $[14] = ButtonComponent;
    $[15] = buttonLabel;
    $[16] = buttonSize;
    $[17] = props.platform.href;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = tooltipLabel;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};